import { PaymentCard } from "./ToolsMobileCards";
import { useToolsMobile } from "./ToolsLayout";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import ToolsLayout, { ListFooter, MarketStatus, RefreshTools, ToolsState } from "./ToolsLayout";
import useToolsData from "./useToolsData";
import { needsReceiptReview, toolsDate } from "./toolsData";
import PaymentReviewDialog from "./PaymentReviewDialog";

export default function ToolsPayments() {
  const mobile = useToolsMobile();
  const query = useToolsData("payments");
  const [params, setParams] = useSearchParams(),
    [page, setPage] = useState(1),
    [review, setReview] = useState(null),
    [notice, setNotice] = useState("");
  const search = params.get("search") || "",
    payment = params.get("paymentStatus") || "",
    status = params.get("orderStatus") || "",
    queue = params.get("review") === "1";
  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };
  const filtered = useMemo(
    () =>
      (query.data || []).filter(
        (row) =>
          (!search ||
            `${row.orderId || ""} ${row.transactionId || ""}`
              .toLowerCase()
              .includes(search.toLowerCase())) &&
          (!payment || row.paymentStatus === payment) &&
          (!status || row.orderStatus === status) &&
          (!queue || needsReceiptReview(row)),
      ),
    [query.data, search, payment, status, queue],
  );
  useEffect(() => {
    setPage((current) => Math.min(current, Math.max(1, Math.ceil(filtered.length / 10))));
  }, [filtered.length]);
  const reset = () => {
    setParams({}, { replace: true });
    setPage(1);
  };
  const hasFilters = Boolean(search || payment || status || queue);
  return (
    <ToolsLayout
      title="Payments & receipt review"
      description="Review payment evidence and verify transaction details against each order."
      actions={<RefreshTools onClick={query.refetch} loading={query.isFetching} />}
    >
      {notice && (
        <Alert severity="success" onClose={() => setNotice("")} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}
      <section className="tools-panel">
        <div className="tools-panel-heading">
          <div>
            <Typography variant="h6" component="h2">
              Payment records
            </Typography>
            <p>
              {query.isLoading
                ? "Loading receipts…"
                : query.isError
                  ? "Data unavailable"
                  : `${filtered.length} matching records · ${(query.data || []).filter(needsReceiptReview).length} receipts awaiting review overall`}
            </p>
          </div>
          <Button
            variant={queue ? "contained" : "outlined"}
            size="small"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete("paymentStatus");
              next.delete("orderStatus");
              queue ? next.delete("review") : next.set("review", "1");
              setParams(next, { replace: true });
              setPage(1);
            }}
          >
            {queue ? "Show all payments" : "Awaiting receipt review"}
          </Button>
        </div>
        <div className="tools-filters">
          <TextField
            size="small"
            label="Search order or transaction ID"
            value={search}
            onChange={(event) => setFilter("search", event.target.value)}
            sx={{ minWidth: 240, flex: 1 }}
          />
          <TextField
            select
            size="small"
            label="Payment status"
            value={payment}
            onChange={(event) => setFilter("paymentStatus", event.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All payments</MenuItem>
            {["pending", "processing", "paid", "rejected"].map((value) => (
              <MenuItem key={value} value={value}>
                {value.charAt(0).toUpperCase() + value.slice(1)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Order status"
            value={status}
            onChange={(event) => setFilter("orderStatus", event.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">All orders</MenuItem>
            {["pending", "accepted", "shipped", "delivered", "rejected", "cancelled"].map(
              (value) => (
                <MenuItem key={value} value={value}>
                  {value.charAt(0).toUpperCase() + value.slice(1)}
                </MenuItem>
              ),
            )}
          </TextField>
          {hasFilters && <Button onClick={reset}>Clear filters</Button>}
        </div>
        <ToolsState
          loading={query.isLoading}
          error={query.isError}
          retry={query.refetch}
          empty={!filtered.length}
          emptyTitle={
            queue
              ? "Receipt review queue is clear"
              : hasFilters
                ? "No matching payments"
                : "No payment records yet"
          }
          emptyMessage={
            hasFilters
              ? "Try another reference or clear the filters to see all payments."
              : "Payments will appear as customers place orders."
          }
          action={hasFilters && <Button onClick={reset}>Clear filters</Button>}
        >
          {mobile ? (
            <div>
              {filtered.slice((page - 1) * 10, page * 10).map((record) => (
                <PaymentCard
                  key={record._id || record.orderId}
                  record={record}
                  onReview={setReview}
                />
              ))}
            </div>
          ) : (
            <TableContainer>
              <Table aria-label="Tool payment receipts">
                <TableHead>
                  <TableRow>
                    <TableCell>Order / Method</TableCell>
                    <TableCell>Order status</TableCell>
                    <TableCell>Payment status</TableCell>
                    <TableCell>Evidence</TableCell>
                    <TableCell>Transaction / Paid date</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.slice((page - 1) * 10, page * 10).map((row) => (
                    <TableRow hover key={row._id || row.orderId}>
                      <TableCell>
                        <Button
                          size="small"
                          component={Link}
                          to={`/home/tools-order/${encodeURIComponent(row.orderId)}`}
                        >
                          {row.orderId}
                        </Button>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.paymentMethod || "Unspecified method"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <MarketStatus status={row.orderStatus} />
                      </TableCell>
                      <TableCell>
                        <MarketStatus status={row.paymentStatus} payment />
                      </TableCell>
                      <TableCell>
                        {row.receiptUrl ? (
                          <Button
                            size="small"
                            href={row.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open receipt
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No file uploaded
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.transactionId || "No transaction reference"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {toolsDate(row.paymentDate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {["pending", "processing"].includes(row.paymentStatus) ? (
                          <Button
                            size="small"
                            variant={needsReceiptReview(row) ? "contained" : "outlined"}
                            onClick={() => setReview(row.orderId)}
                          >
                            {row.receiptUrl ? "Review payment" : "Record payment"}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            component={Link}
                            to={`/home/tools-order/${encodeURIComponent(row.orderId)}`}
                          >
                            View details
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <ListFooter page={page} count={filtered.length} pageSize={10} setPage={setPage} />
        </ToolsState>
      </section>
      {review && (
        <PaymentReviewDialog
          key={review}
          orderId={review}
          onClose={() => setReview(null)}
          onSaved={setNotice}
        />
      )}
    </ToolsLayout>
  );
}
